import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import { Big } from 'big.js';
import * as _ from 'lodash';

// PRE-4896 : builder dédié à la slide "Taux par support" (versement complémentaire / programmé).
// Conserve tous les champs déjà présents sur chaque support (amount, percentage, ...), à
// l'identique du pattern déjà utilisé pour préserver ces champs dans isin-table.
//
// Les 4 champs "*Saisi" sont affichés via un input brut (eaNumericInput), le FormControl porte donc
// directement la valeur affichée en pourcentage (0-100), d'où la conversion *100/100 à
// l'initialisation/l'extraction (toPercent/fromPercent), à l'identique de taux-par-support.
// Big plutôt que Math.round : évite le cas classique où l'étape intermédiaire *100000 est elle-même
// imprécise en flottant et fait arrondir dans le mauvais sens (cf. isin-table/ea-subscription-form.builder.ts).
function toPercent(value: number | null | undefined): number | null {
  return value != null ? new Big(value).times(100).round(3).toNumber() : null;
}
function fromPercent(value: number | string | null | undefined): number | null {
  return value != null && value !== '' ? +value / 100 : null;
}

@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = { ...option };
    const options = {};

    const rows: UntypedFormGroup[] = [];
    const entitySupportRepartition =
      entity?.parameters?.supportsInvestment?.supportRepartition || [];

    _.forEach(entitySupportRepartition, item =>
      rows.push(this.formBuilder.group(this.createModel(item))),
    );

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        supportRepartition: this.formBuilder.array(
          rows,
          Validators.nullValidator,
        ),
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportRepartition() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportRepartition') as UntypedFormArray;
  }

  public createModel(item) {
    return {
      supportId: [item ? item.supportId : null],
      supportName: [item ? item.supportName : null],
      isin: [item ? item.isin : null],
      isr: [item ? item.isr : null],
      amount: [item && !_.isNil(item.amount) ? item.amount : 0],
      percentage: [item ? item.percentage : null],
      supportType: [item ? item.supportType : null],
      index: [item?.index ?? null],
      perfP3Y: [item?.perfP3Y ?? null],
      volat3Y: [item?.volat3Y ?? null],
      risk: [item?.risk ?? null],
      // Préremplis par la commande COMPLETE_SUPPORT_RATES (résolution base support), jamais saisis
      // directement — servent de placeholder et de valeur par défaut du champ "Saisi" ci-dessous.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      // Valeur effectivement retenue : initialisée depuis la valeur déjà saisie (rejeu du
      // formulaire) sinon depuis la valeur préremplie ci-dessus, convertie en pourcentage (0-100)
      // pour l'affichage. Désactivée en lecture seule pour les organisations affiliées EUODIA (cf.
      // taux-par-support-versement.component.ts).
      upfrontAssureurSaisi: [
        toPercent(item?.upfrontAssureurSaisi ?? item?.upfrontAssureurAutocomplete),
      ],
      upfrontEmetteurSaisi: [
        toPercent(item?.upfrontEmetteurSaisi ?? item?.upfrontEmetteurAutocomplete),
      ],
      fraisGestionSaisi: [
        toPercent(item?.fraisGestionSaisi ?? item?.fraisGestionAutocomplete),
      ],
      retrocessionAssureurSaisi: [
        toPercent(
          item?.retrocessionAssureurSaisi ??
            item?.retrocessionAssureurAutocomplete,
        ),
      ],
    };
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      const supportRepartition = (
        product.parameters.supportRepartition || []
      ).map(row => ({
        ...row,
        upfrontAssureurSaisi: fromPercent(row.upfrontAssureurSaisi),
        upfrontEmetteurSaisi: fromPercent(row.upfrontEmetteurSaisi),
        fraisGestionSaisi: fromPercent(row.fraisGestionSaisi),
        retrocessionAssureurSaisi: fromPercent(row.retrocessionAssureurSaisi),
      }));

      const totalAmount = supportRepartition.reduce(
        (acc, item) => acc + (item && item.amount ? Number(item.amount) : 0),
        0,
      );

      product.parameters.supportRepartition = undefined;
      product.parameters.supportsInvestment = {
        totalAmount,
        supportRepartition,
      };
    });
    return products;
  }
}
